import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { selectLatestStatement } from '@/helpers/dashboardStatementShortcut'
import { periodKeyToTaxYearLabel } from '@/helpers/periodDisplay'
import debug from '@/helpers/debug'
import type { UserStatement } from '@/types/userStatement'

/**
 * Latest issued statement for the dashboard shortcut.
 * The home page does not otherwise load statements, and free donors cannot
 * use the donations endpoint, so this fetches GET /me/statements once.
 */
export function useDashboardStatementShortcut() {
  const store = useStore()

  const statements = computed(
    () => store.getters.userStatements as UserStatement[] | null
  )

  const latestStatement = computed(() =>
    statements.value ? selectLatestStatement(statements.value) : null
  )

  const taxYearLabel = computed(() =>
    latestStatement.value
      ? periodKeyToTaxYearLabel(latestStatement.value.period)
      : ''
  )

  // A failed fetch leaves statements null, so track when the lookup is done.
  const lookupDone = ref(statements.value !== null)

  const isLoading = computed(
    () => statements.value === null && !lookupDone.value
  )

  if (isLoading.value) {
    debug('[quick-actions] fetching statements for the dashboard shortcut')
    void store.dispatch('fetchUserStatements').finally(() => {
      lookupDone.value = true
    })
  }

  watch(latestStatement, (statement) => {
    debug('[quick-actions] statement shortcut', {
      period: statement?.period ?? null,
      totalDonated: statement?.totalDonated ?? null,
    })
  })

  return {
    latestStatement,
    taxYearLabel,
    isLoading,
  }
}
